import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLayer } from './layers';

export interface Anchor {
  x: number;
  y: number;
  /** Breite/Höhe des Ankers (Element-Rechteck), 0 bei Mausposition. */
  w?: number;
  h?: number;
}

export function anchorFrom(el: Element, ): Anchor {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

interface Props {
  anchor: Anchor;
  onClose: () => void;
  children: ReactNode;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start' | 'left-start';
  className?: string;
  testId?: string;
  role?: string;
  label?: string;
}

/** Schwebendes Element an einem Anker, bleibt im Viewport, schließt bei Klick außerhalb/Escape. */
export function Popover({ anchor, onClose, children, placement = 'bottom-start', className = '', testId = 'popover', role = 'dialog', label }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  /** Erhöht sich bei Größenänderung des Viewports (z. B. Bildschirmtastatur) → neu positionieren statt schließen. */
  const [viewportTick, setViewportTick] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const aw = anchor.w ?? 0;
    const ah = anchor.h ?? 0;
    let left = anchor.x;
    let top = anchor.y + ah + 6;
    if (placement.startsWith('top')) top = anchor.y - height - 6;
    if (placement.endsWith('end')) left = anchor.x + aw - width;
    if (placement === 'right-start') {
      left = anchor.x + aw + 8;
      top = anchor.y;
    }
    if (placement === 'left-start') {
      left = anchor.x - width - 8;
      top = anchor.y;
    }
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
    if (left < 8) left = 8;
    if (top + height > vh - 8) top = placement.startsWith('bottom') && anchor.y - height - 6 > 8 ? anchor.y - height - 6 : Math.max(8, vh - height - 8);
    if (top < 8) top = 8;
    setPos({ left, top });
  }, [anchor.x, anchor.y, anchor.w, anchor.h, placement, viewportTick]);
  const isTop = useLayer();
  // onClose ist meist inline – über eine Ref lesen, damit die Listener nicht bei jedem Rendern neu entstehen.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      // Klicks in darüberliegende Ebenen (z. B. ein Picker aus diesem Popover) schließen nicht.
      if (ref.current && !ref.current.contains(e.target as Node) && isTop()) closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isTop()) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    const onResize = () => setViewportTick((n) => n + 1);
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [isTop]);
  return createPortal(
    <div
      ref={ref}
      className={`popover ${className}`}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}
      data-testid={testId}
      role={role}
      aria-label={label}
    >
      {children}
    </div>,
    document.body,
  );
}

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  separatorBefore?: boolean;
  hidden?: boolean;
}

/** Kontextmenü mit Tastatursteuerung (Pfeile, Enter, Escape). */
export function Menu({ anchor, items, onClose, testId = 'menu', placement }: { anchor: Anchor; items: MenuItem[]; onClose: () => void; testId?: string; placement?: Props['placement'] }) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus();
  }, []);
  const onKey = (e: React.KeyboardEvent) => {
    const els = [...(listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])];
    const i = els.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[(i + 1) % els.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      els[(i - 1 + els.length) % els.length]?.focus();
    }
  };
  return (
    <Popover anchor={anchor} onClose={onClose} testId={testId} role="presentation" placement={placement}>
      <div className="menu" role="menu" ref={listRef} onKeyDown={onKey}>
        {items
          .filter((it) => !it.hidden)
          .map((it) => (
            <div key={it.id} style={{ display: 'contents' }}>
              {it.separatorBefore && <div className="menu-sep" role="separator" />}
              <button
                role="menuitem"
                className={`menu-item ${it.danger ? 'danger' : ''}`}
                disabled={it.disabled}
                data-testid={`${testId}-${it.id}`}
                onClick={() => {
                  onClose();
                  it.onSelect?.();
                }}
              >
                {it.icon}
                <span>{it.label}</span>
              </button>
            </div>
          ))}
      </div>
    </Popover>
  );
}
